import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { SpecialtySelectorComboBox } from '@eluve/blocks';
import {
  Alert,
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icon,
  Stack,
  Text,
  Textarea,
} from '@eluve/components';
import { SupportedLanguages } from '@eluve/language-utils';
import { LlmOutputTypeLabels } from '@eluve/llm-outputs';

import type { ScopeType } from './components';
import type { PromptTemplateReference } from './promptTemplateDefaults.operations';

export interface LlmOutputType {
  __typename: 'LlmOutputTypes';
  name: string;
}

export interface OutputTemplateOption {
  id: string;
  name: string;
  llmOutputType: string;
}

export const promptTemplateDefaultFormSchema = z
  .object({
    scope: z.enum(['global', 'tenant', 'user']),
    tenantId: z.string().uuid().nullable(),
    userId: z.string().uuid().nullable(),
    llmOutputType: z.string().min(1, 'Output type is required'),
    specialty: z.string().nullable(),
    language: z.string().nullable(),
    promptTemplateId: z.string().uuid('Prompt template is required'),
    outputTemplateId: z.string().uuid().nullable(),
    customInstructions: z.string().nullable(),
  })
  .refine(
    (data) => {
      if (data.scope === 'tenant' || data.scope === 'user') {
        return data.tenantId !== null;
      }
      return true;
    },
    {
      message: 'Organization is required for tenant or user scope',
      path: ['tenantId'],
    },
  )
  .refine(
    (data) => {
      if (data.scope === 'user') {
        return data.userId !== null;
      }
      return true;
    },
    {
      message: 'User is required for user scope',
      path: ['userId'],
    },
  );

export type PromptTemplateDefaultFormValues = z.infer<
  typeof promptTemplateDefaultFormSchema
>;

// Check if a template supports custom instructions
const templateSupportsCustomInstructions = (
  template: PromptTemplateReference | undefined,
): boolean => {
  const variant = template?.prompt_template_variants?.[0];
  if (!variant?.template) return true; // Assume it does if no template data
  const templateString = JSON.stringify(variant.template);
  return templateString.includes('{{system_custom_instruction}}');
};

interface PromptTemplateDefaultFormProps {
  defaultValues?: Partial<PromptTemplateDefaultFormValues>;
  onSubmit: (values: PromptTemplateDefaultFormValues) => void;
  isLoading?: boolean;
  fixedScope: ScopeType;
  fixedTenantId?: string;
  fixedUserId?: string;
  llmOutputTypes: LlmOutputType[];
  promptTemplates: PromptTemplateReference[];
  outputTemplates?: OutputTemplateOption[];
  formRef?: React.RefObject<HTMLFormElement | null>;
  hideSubmitButton?: boolean;
  onRefreshTemplates?: () => void;
  disableOutputType?: boolean;
  disabledOutputTypes?: Record<string, string>;
  hideSpecialty?: boolean;
}

export const PromptTemplateDefaultForm: React.FC<
  PromptTemplateDefaultFormProps
> = ({
  defaultValues,
  onSubmit,
  isLoading = false,
  fixedScope,
  fixedTenantId,
  fixedUserId,
  llmOutputTypes,
  promptTemplates,
  outputTemplates = [],
  formRef,
  hideSubmitButton = false,
  onRefreshTemplates,
  disableOutputType = false,
  disabledOutputTypes = {},
  hideSpecialty = false,
}) => {
  const form = useForm<PromptTemplateDefaultFormValues>({
    resolver: zodResolver(promptTemplateDefaultFormSchema),
    defaultValues: {
      scope: fixedScope,
      tenantId: fixedTenantId ?? null,
      userId: fixedUserId ?? null,
      llmOutputType: '',
      specialty: null,
      language: null,
      promptTemplateId: '',
      outputTemplateId: null,
      customInstructions: null,
      ...defaultValues,
    },
  });

  const selectedLlmOutputType = form.watch('llmOutputType');
  const isDynamicOutput = selectedLlmOutputType === 'DYNAMIC_OUTPUT';
  const isSoapNote = selectedLlmOutputType === 'SOAP_NOTE';
  const isTenantOrUserScope = fixedScope === 'tenant' || fixedScope === 'user';

  // Filter prompt templates by output type if one is selected
  const filteredPromptTemplates = selectedLlmOutputType
    ? promptTemplates.filter((pt) => pt.outputType === selectedLlmOutputType)
    : promptTemplates;

  // Show all available output templates when DYNAMIC_OUTPUT is selected
  // Output templates define the output structure, not filtered by the selected output type
  const filteredOutputTemplates = isDynamicOutput ? outputTemplates : [];

  const handleSubmit = (values: PromptTemplateDefaultFormValues) => {
    // Normalize empty strings to null
    const normalizedValues = {
      ...values,
      specialty: values.specialty?.trim() || null,
      language: values.language?.trim() || null,
      // Clear output template if not DYNAMIC_OUTPUT
      outputTemplateId:
        values.llmOutputType === 'DYNAMIC_OUTPUT'
          ? (values.outputTemplateId ?? null)
          : null,
      customInstructions: values.customInstructions?.trim() || null,
    };
    onSubmit(normalizedValues);
  };

  return (
    <Form {...form}>
      <form ref={formRef} onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack gap={3} align="stretch" className="text-sm">
          {/* LLM Output Type */}
          <FormField
            control={form.control}
            name="llmOutputType"
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>Output Type</FormLabel>
                {disableOutputType ? (
                  <div className="rounded-md border border-border-tertiary bg-bg-tertiary px-3 py-2">
                    <Text size="xs" color="secondary">
                      {field.value
                        ? (LlmOutputTypeLabels[
                            field.value as keyof typeof LlmOutputTypeLabels
                          ] ?? field.value)
                        : 'No output type'}
                    </Text>
                  </div>
                ) : (
                  <Combobox>
                    <ComboboxSelectButton className="w-full rounded-md border border-border-primary hover:bg-bg-quaternary">
                      <Stack gap={2} direction="horizontal">
                        {field.value
                          ? (LlmOutputTypeLabels[
                              field.value as keyof typeof LlmOutputTypeLabels
                            ] ?? field.value)
                          : 'Select Output Type'}
                      </Stack>
                    </ComboboxSelectButton>
                    <ComboboxDropdown searchPlaceholder="Search output types...">
                      {llmOutputTypes.map((outputType) => {
                        const disabledReason =
                          disabledOutputTypes[outputType.name];
                        if (disabledReason) {
                          return (
                            <div
                              key={outputType.name}
                              className="cursor-not-allowed px-2 py-1.5 opacity-50"
                            >
                              <Stack gap={2} direction="horizontal">
                                <div className="mt-0.5 mr-2 size-4 shrink-0 self-start" />
                                <Stack>
                                  <Text inline size="xs">
                                    {LlmOutputTypeLabels[
                                      outputType.name as keyof typeof LlmOutputTypeLabels
                                    ] ?? outputType.name}
                                  </Text>
                                  <Text inline size="xs" color="tertiary">
                                    {disabledReason}
                                  </Text>
                                </Stack>
                              </Stack>
                            </div>
                          );
                        }
                        return (
                          <ComboboxOption
                            key={outputType.name}
                            onSelect={() => {
                              field.onChange(outputType.name);
                              // Clear prompt template and output template when output type changes
                              form.setValue('promptTemplateId', '');
                              form.setValue('outputTemplateId', null);
                            }}
                          >
                            <ComboboxSelectCheck
                              className="mt-0.5 mr-2 self-start"
                              selected={field.value === outputType.name}
                            />
                            <Stack>
                              <Text size="xs">
                                {LlmOutputTypeLabels[
                                  outputType.name as keyof typeof LlmOutputTypeLabels
                                ] ?? outputType.name}
                              </Text>
                              <Text size="xs" color="tertiary">
                                {outputType.name}
                              </Text>
                            </Stack>
                          </ComboboxOption>
                        );
                      })}
                    </ComboboxDropdown>
                  </Combobox>
                )}
                {error && <FormMessage>{error.message}</FormMessage>}
                {!disableOutputType && isTenantOrUserScope && (
                  <FormDescription>
                    Only output types enabled for this organization are shown
                  </FormDescription>
                )}
                {isSoapNote && isTenantOrUserScope && (
                  <Alert variant="warning" className="mt-2">
                    <Icon
                      name="AlertTriangle"
                      className="mt-0.5 shrink-0 self-start"
                    />
                    <Stack gap={1} align="start">
                      <Text size="xs">SOAP_NOTE is deprecated</Text>
                      <Text size="xs" color="secondary">
                        DYNAMIC_OUTPUT is the preferred output type. Only use
                        SOAP_NOTE if you have a specific reason to do so.
                      </Text>
                    </Stack>
                  </Alert>
                )}
              </FormItem>
            )}
          />

          {/* Specialty and Language */}
          <Stack gap={3} align="start" direction="horizontal">
            {!hideSpecialty && (
              <FormField
                control={form.control}
                name="specialty"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Specialty (optional)</FormLabel>
                    <SpecialtySelectorComboBox
                      selectedSpecialty={field.value}
                      onSelectedSpecialty={field.onChange}
                      placeholder="Any specialty"
                    />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Language (optional)</FormLabel>
                  <Combobox>
                    <ComboboxSelectButton className="w-full rounded-md border border-border-primary hover:bg-bg-quaternary">
                      <Stack gap={2} direction="horizontal">
                        {field.value
                          ? (SupportedLanguages[field.value] ?? field.value)
                          : 'Any language'}
                      </Stack>
                    </ComboboxSelectButton>
                    <ComboboxDropdown searchPlaceholder="Search languages...">
                      <ComboboxOption onSelect={() => field.onChange(null)}>
                        <ComboboxSelectCheck
                          className="mr-2"
                          selected={field.value === null}
                        />
                        Any language
                      </ComboboxOption>
                      {Object.entries(SupportedLanguages).map(
                        ([code, name]) => (
                          <ComboboxOption
                            key={code}
                            onSelect={() => field.onChange(code)}
                          >
                            <ComboboxSelectCheck
                              className="mr-2"
                              selected={field.value === code}
                            />
                            {name}
                          </ComboboxOption>
                        ),
                      )}
                    </ComboboxDropdown>
                  </Combobox>
                </FormItem>
              )}
            />
          </Stack>

          {/* Prompt Template */}
          <FormField
            control={form.control}
            name="promptTemplateId"
            render={({ field, fieldState: { error } }) => {
              const selectedTemplate = filteredPromptTemplates.find(
                (pt) => pt.id === field.value,
              );
              const supportsCustomInstructions =
                templateSupportsCustomInstructions(selectedTemplate);

              return (
                <FormItem>
                  <FormLabel>Prompt Template</FormLabel>
                  <Combobox>
                    <ComboboxSelectButton className="w-full rounded-md border border-border-primary hover:bg-bg-quaternary">
                      <Stack gap={2} direction="horizontal">
                        {selectedTemplate?.name ?? 'Select Prompt Template'}
                      </Stack>
                    </ComboboxSelectButton>
                    <ComboboxDropdown searchPlaceholder="Search templates...">
                      {filteredPromptTemplates.length === 0 ? (
                        <Text size="xs" color="secondary" className="p-2">
                          {selectedLlmOutputType
                            ? 'No active templates for this output type'
                            : 'Select an output type first'}
                        </Text>
                      ) : (
                        filteredPromptTemplates.map((template) => (
                          <ComboboxOption
                            key={template.id}
                            onSelect={() => field.onChange(template.id)}
                          >
                            <ComboboxSelectCheck
                              className="mr-2"
                              selected={field.value === template.id}
                            />
                            {template.name}
                          </ComboboxOption>
                        ))
                      )}
                    </ComboboxDropdown>
                  </Combobox>
                  {error && <FormMessage>{error.message}</FormMessage>}
                  {(fixedScope === 'tenant' || fixedScope === 'user') && (
                    <FormDescription>
                      Only templates enabled for this organization are shown
                    </FormDescription>
                  )}
                  {selectedTemplate && !supportsCustomInstructions && (
                    <Alert variant="warning" className="mt-2">
                      <Icon
                        name="AlertTriangle"
                        className="mt-0.5 shrink-0 self-start"
                      />
                      <Stack gap={1} align="start">
                        <Text size="xs">
                          This template does not support custom instructions
                        </Text>
                        <Text size="xs" color="secondary">
                          Add{' '}
                          <code className="rounded-sm bg-bg-tertiary px-1 py-0.5 font-mono text-xs">
                            {'{{system_custom_instruction}}'}
                          </code>{' '}
                          to the system, user, or assistant prompt to enable
                          this feature.
                        </Text>
                        <Stack gap={3} direction="horizontal">
                          <a
                            href={`/admin/prompts/${selectedTemplate.id}/prompt`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-text-secondary underline decoration-dashed underline-offset-2"
                          >
                            Edit template
                            <Icon
                              name="ExternalLink"
                              size="sm"
                              className="size-3"
                            />
                          </a>
                          {onRefreshTemplates && (
                            <button
                              type="button"
                              onClick={onRefreshTemplates}
                              className="inline-flex items-center gap-1 text-sm text-text-secondary underline decoration-dashed underline-offset-2"
                            >
                              Check again
                              <Icon
                                name="RefreshCw"
                                size="sm"
                                className="size-3"
                              />
                            </button>
                          )}
                        </Stack>
                      </Stack>
                    </Alert>
                  )}
                  {selectedTemplate && supportsCustomInstructions && (
                    <Stack
                      gap={2}
                      className="mt-2 rounded-lg border border-border-positive-subtle bg-bg-positive-subtle p-3 text-text-positive-subtle"
                      direction="horizontal"
                    >
                      <Icon name="CheckCircle2" className="shrink-0" />
                      <Text inline size="xs">
                        This template supports custom instructions
                      </Text>
                    </Stack>
                  )}
                </FormItem>
              );
            }}
          />

          {/* Output Template (only for DYNAMIC_OUTPUT) */}
          {isDynamicOutput && (
            <FormField
              control={form.control}
              name="outputTemplateId"
              render={({ field }) => {
                const selectedOutputTemplate = filteredOutputTemplates.find(
                  (ot) => ot.id === field.value,
                );

                return (
                  <FormItem>
                    <FormLabel>Output Template</FormLabel>
                    <Combobox>
                      <ComboboxSelectButton className="w-full rounded-md border border-border-primary hover:bg-bg-quaternary">
                        <Stack gap={2} direction="horizontal">
                          {selectedOutputTemplate?.name ??
                            'Select Output Template'}
                        </Stack>
                      </ComboboxSelectButton>
                      <ComboboxDropdown searchPlaceholder="Search output templates...">
                        <ComboboxOption onSelect={() => field.onChange(null)}>
                          <ComboboxSelectCheck
                            className="mr-2"
                            selected={field.value === null}
                          />
                          None
                        </ComboboxOption>
                        {filteredOutputTemplates.map((template) => (
                          <ComboboxOption
                            key={template.id}
                            onSelect={() => field.onChange(template.id)}
                          >
                            <ComboboxSelectCheck
                              className="mr-2"
                              selected={field.value === template.id}
                            />
                            {template.name}
                          </ComboboxOption>
                        ))}
                      </ComboboxDropdown>
                    </Combobox>
                    {(fixedScope === 'tenant' || fixedScope === 'user') && (
                      <FormDescription>
                        Only output templates enabled for this organization are
                        shown
                      </FormDescription>
                    )}
                  </FormItem>
                );
              }}
            />
          )}

          {/* Custom Instructions */}
          <FormField
            control={form.control}
            name="customInstructions"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Custom Instructions (optional)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Add any custom instructions to be appended to the prompt template..."
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    rows={4}
                  />
                </FormControl>
                <FormDescription>
                  These instructions will be appended to the selected prompt
                  template when generating outputs
                </FormDescription>
              </FormItem>
            )}
          />

          {!hideSubmitButton && (
            <Button
              size="lg"
              disabled={isLoading || !form.formState.isDirty}
              fullWidth
              type="submit"
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Stack>
      </form>
    </Form>
  );
};
